/**
 * ClientFormDialog Component
 *
 * Dialog for creating and editing clients.
 * Uses controlled form state with validation.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AliasesInput } from './AliasesInput';
import { ContactsInput } from './ContactsInput';
import type { Client, ClientFormData } from '@/types/management';

interface ClientFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: Client | null;
  onSubmit: (data: ClientFormData) => Promise<void>;
}

const initialFormData: ClientFormData = {
  name: '',
  aliases: [],
  keywords: [],
  contacts: [],
  description: '',
};

export function ClientFormDialog({
  open,
  onOpenChange,
  client,
  onSubmit,
}: ClientFormDialogProps) {
  const [formData, setFormData] = useState<ClientFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!client;

  // Reset form when dialog opens/closes or client changes
  useEffect(() => {
    if (open) {
      if (client) {
        setFormData({
          name: client.name,
          aliases: client.aliases,
          keywords: client.keywords || [],
          contacts: client.contacts || [],
          description: client.description || '',
        });
      } else {
        setFormData(initialFormData);
      }
      setError(null);
    }
  }, [open, client]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      // Validate required fields
      if (!formData.name.trim()) {
        setError('Name is required');
        return;
      }

      setIsSubmitting(true);
      try {
        await onSubmit({
          ...formData,
          name: formData.name.trim(),
          description: formData.description?.trim() || undefined,
        });
        onOpenChange(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save client');
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, onSubmit, onOpenChange],
  );

  const handleChange = useCallback(
    (field: keyof ClientFormData, value: string | string[]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (error) setError(null);
    },
    [error],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Edit Client' : 'Add Client'}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the client information below.'
                : 'Enter the details for the new client.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Name */}
            <div className="grid gap-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Client name"
                disabled={isSubmitting}
                autoFocus
              />
            </div>

            {/* Aliases */}
            <div className="grid gap-2">
              <Label>Aliases</Label>
              <p className="text-xs text-muted-foreground">
                Alternative names for this client
              </p>
              <AliasesInput
                value={formData.aliases}
                onChange={(aliases) => handleChange('aliases', aliases)}
                placeholder="Add alias and press Enter"
                disabled={isSubmitting}
              />
            </div>

            {/* Keywords */}
            <div className="grid gap-2">
              <Label>Keywords</Label>
              <p className="text-xs text-muted-foreground">
                Terms for classification: products, people, project names
              </p>
              <AliasesInput
                value={formData.keywords}
                onChange={(keywords) => handleChange('keywords', keywords)}
                placeholder="Add keyword and press Enter"
                disabled={isSubmitting}
              />
            </div>

            {/* Contacts */}
            <div className="grid gap-2">
              <Label>Contacts</Label>
              <p className="text-xs text-muted-foreground">
                Known email addresses (e.g., john@acme.com). Used to match
                meeting participants.
              </p>
              <ContactsInput
                value={formData.contacts}
                onChange={(contacts) => handleChange('contacts', contacts)}
                placeholder="Add email and press Enter"
                disabled={isSubmitting}
              />
            </div>

            {/* Description */}
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Optional description"
                disabled={isSubmitting}
                rows={3}
              />
            </div>

            {/* Error Display */}
            {error && (
              <div className="text-sm text-destructive" role="alert">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? 'Saving...'
                : isEditing
                  ? 'Save Changes'
                  : 'Add Client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
